import { createRef } from 'react';
import Cookies from 'universal-cookie';

import { isDev } from '~/dev/utils/isDev';
import { isProd } from '~/dev/utils/isProd';
import { StorageService } from '~/services/StorageService/StorageService';
import { getWildDomain } from '~/utils/browser';
import { testIsValidWindowOpener } from '~/utils/dom';

// TODO: DEPRECATE Next 3 lines - EKP-15824
const access_token = 'access_token';
const refresh_token = 'refresh_token';

// Cookies
const LOGGED_IN_COOKIE = 'logged_in_cookie';
const EXPIRES_AT_COOKIE = 'expires_at_cookie';
const IDLE_TIMEOUT_COOKIE = 'idle_timeout_cookie';
// Local Storage
const inactive_expires_at = 'inactive_expires_at';
const access_token_created_at = 'access_token_created_at';
const session_subdomain = 'session_subdomain';
const IS_WORKDAY_USER_KEY = 'is_workday_user';
// Session Storage
const zendesk_redirect_url = 'zendesk_redirect_url';
const zendesk_redirect_expire = 'zendesk_redirect_expire';
const oauth_redirect_uri = 'oauth_redirect_uri';
const oauth_client_id = 'oauth_client_id';
const oauth_state = 'oauth_state';
const oauth_redirect_expire = 'oauth_redirect_expire';

const CSRF_TOKEN_REFERENCE = createRef();

const AuthenticationStore = () => {
  const defaultOptions = {
    domain: getWildDomain(window.location.hostname),
    path: '/',
    // Set to None for embedded sessions. For local dev,
    // we set to Lax to allow cookies to be set.
    sameSite: isDev ? 'Lax' : 'None',
    // Secure is set to false for local dev to allow cookies to be set.
    secure: !isDev,
  };

  const constants = {
    MS_UNTIL_ZENDESK_EXPIRE: 5 * 60 * 1000,
    MS_UNTIL_OAUTH_EXPIRE: 5 * 60 * 1000,
    S_UNTIL_INACTIVE_TIMEOUT: 3600,
  };

  const cookie = new Cookies();

  // we consider the session embedded (e.g. in an iframe) if the window is not the current/top window, or if it has been opened by another window.
  function isEmbed() {
    if (isProd) {
      return window.self !== window.top || testIsValidWindowOpener();
    }
    return false;
  }

  function getStoredLoggedInCookie() {
    return cookie.get(LOGGED_IN_COOKIE, defaultOptions);
  }

  function getStoredExpiresAtCookie() {
    return cookie.get(EXPIRES_AT_COOKIE, defaultOptions);
  }

  function getUserConfigInactivityTimeout() {
    const timeout = cookie.get(IDLE_TIMEOUT_COOKIE, defaultOptions);

    return timeout ? parseInt(timeout, 10) : timeout;
  }

  // Priority: user config > client config > default constant
  function getInactiveTimeoutInSeconds(clientConfigTimeout) {
    const userConfigTimeout = getUserConfigInactivityTimeout();

    if (userConfigTimeout && userConfigTimeout > 0) {
      return userConfigTimeout;
    }

    return clientConfigTimeout && clientConfigTimeout > 0
      ? clientConfigTimeout
      : constants.S_UNTIL_INACTIVE_TIMEOUT;
  }

  function getIsWorkdayUser() {
    return StorageService.getItem(IS_WORKDAY_USER_KEY);
  }
  function setIsWorkdayUser(isWorkdayUser) {
    StorageService.setItem(IS_WORKDAY_USER_KEY, String(isWorkdayUser));
  }
  function deleteIsWorkdayUser() {
    StorageService.removeItem(IS_WORKDAY_USER_KEY);
  }

  function saveAccessToken(accessToken) {
    cookie.set(access_token, accessToken, defaultOptions);
  }

  function removeInactiveTimer() {
    StorageService.removeItem(inactive_expires_at);
  }

  function resetInactiveTimer(timeoutInSecond) {
    StorageService.setItem(
      inactive_expires_at,
      String(Date.now() + timeoutInSecond * 1000),
    );
  }

  function saveAuthentication(accessToken, refreshToken) {
    clearAuthentication();

    cookie.set(access_token, accessToken, defaultOptions);

    const maxAge = parseInt(getStoredExpiresAtCookie(), 10);
    cookie.set(refresh_token, refreshToken, {
      ...defaultOptions,
      maxAge:
        !Number.isNaN(maxAge) && !isDev
          ? maxAge - Math.ceil(Date.now() / 1000)
          : undefined,
    });

    removeInactiveTimer();
    saveJwtTokenCreationTime();
  }

  function clearAuthentication() {
    StorageService.removeItem(inactive_expires_at);
    deleteIsWorkdayUser();
    // for non-httponly auth path
    cookie.remove(access_token, defaultOptions);
    cookie.remove(refresh_token, defaultOptions);
    // for httponly auth path
    cookie.remove(LOGGED_IN_COOKIE, defaultOptions);

    // set csrf to null so it can refetch
    setCsrf(null);
  }

  function getAccessToken() {
    return cookie.get(access_token, defaultOptions);
  }

  function getRefreshToken() {
    return cookie.get(refresh_token, defaultOptions);
  }

  function isAuthSessionExpired() {
    if (isDev) return false;
    const expiresAt = getStoredExpiresAtCookie();
    if (!expiresAt) return true;

    return Math.ceil(Date.now() / 1000) > parseInt(expiresAt, 10);
  }

  function isInactiveTimeoutReached() {
    const inactiveExpireTime = StorageService.getItem(inactive_expires_at);
    if (!inactiveExpireTime) return false;

    return Date.now() > parseInt(inactiveExpireTime, 10);
  }

  function shouldConsiderSessionValid() {
    if (getStoredExpiresAtCookie() && !isDev) {
      return !isAuthSessionExpired();
    }
    return true;
  }

  function shouldConsiderUserLoggedIn() {
    // for non-httponly auth path - TODO: DEPRECATE Next line - EKP-15824
    const hasValidTokens = getAccessToken() && getRefreshToken();
    // for httponly auth path
    const hasLoggedInFlag = getStoredLoggedInCookie();

    return hasValidTokens || hasLoggedInFlag;
  }

  function shouldConsiderSameCell() {
    if (isDev) return true;

    const cookieValue = getStoredLoggedInCookie();
    if (!cookieValue || typeof cookieValue !== 'string') return true;

    const dotIndex = cookieValue.indexOf('.');
    const cookieCell = dotIndex > -1 ? cookieValue.slice(dotIndex + 1) : '';
    const hostname = window.location.hostname;

    if (!cookieCell || !hostname.startsWith('clients.')) return true;

    return (
      cookieCell.toLowerCase() ===
      hostname.slice('clients.'.length).toLowerCase()
    );
  }

  function isRolodexAuthed() {
    const cookie = getStoredLoggedInCookie();
    return cookie?.toUpperCase().includes('ROLODEX') ?? false;
  }

  function isAegisAuthed() {
    const cookie = getStoredLoggedInCookie();
    return cookie?.toUpperCase().includes('AEGIS') ?? false;
  }

  function isLoggedIn() {
    return (
      shouldConsiderUserLoggedIn() &&
      shouldConsiderSessionValid() &&
      shouldConsiderSameCell()
    );
  }

  function getSubDomainInSession() {
    return StorageService.getItem(session_subdomain);
  }

  function saveSubDomainInSession(subdomain) {
    StorageService.setItem(session_subdomain, subdomain);
  }

  function getZendeskUrl() {
    return sessionStorage.getItem(zendesk_redirect_url);
  }

  function getZendeskExpire() {
    return sessionStorage.getItem(zendesk_redirect_expire);
  }

  const setZendeskUrl = (redirectUrl) => {
    const zendeskRedirectExp = (
      new Date().valueOf() + constants.MS_UNTIL_ZENDESK_EXPIRE
    ).toString();

    sessionStorage.setItem(zendesk_redirect_url, redirectUrl);
    sessionStorage.setItem(zendesk_redirect_expire, zendeskRedirectExp);
  };

  const removeZendeskFlag = () => {
    sessionStorage.removeItem(zendesk_redirect_url);
    sessionStorage.removeItem(zendesk_redirect_expire);
  };

  function getOAuthExpire() {
    return sessionStorage.getItem(oauth_redirect_expire);
  }

  function getOAuthVars() {
    return [
      sessionStorage.getItem(oauth_redirect_uri),
      sessionStorage.getItem(oauth_client_id),
      sessionStorage.getItem(oauth_state),
    ];
  }

  const setOAuthVars = (redirectUri, clientId, state) => {
    const oauthRedirectExp = (
      new Date().valueOf() + constants.MS_UNTIL_OAUTH_EXPIRE
    ).toString();

    sessionStorage.setItem(oauth_redirect_uri, redirectUri);
    sessionStorage.setItem(oauth_client_id, clientId);
    sessionStorage.setItem(oauth_state, state);
    sessionStorage.setItem(oauth_redirect_expire, oauthRedirectExp);
  };

  const removeOAuthFlag = () => {
    sessionStorage.removeItem(oauth_redirect_uri);
    sessionStorage.removeItem(oauth_client_id);
    sessionStorage.removeItem(oauth_state);
    sessionStorage.removeItem(oauth_redirect_expire);
  };

  const saveJwtTokenCreationTime = () => {
    StorageService.setItem(
      access_token_created_at,
      String(new Date().getTime()),
    );
  };

  const getAccessTokenCreationTime = () => {
    const tokenCreationTime = StorageService.getItem(access_token_created_at);
    return tokenCreationTime ? parseInt(tokenCreationTime, 10) : null;
  };

  const setCsrf = (token) => {
    CSRF_TOKEN_REFERENCE.current = token;
    if (token) {
      StorageService.setEncryptionKey(token);
    }
  };

  const getCsrf = () => {
    return CSRF_TOKEN_REFERENCE.current;
  };

  const getCacheEncryptionKey = () => {
    return getCsrf();
  };

  return {
    // Constants
    constants,

    // Authentication core methods
    clearAuthentication,
    saveAuthentication,

    // Access token methods
    getAccessToken,
    saveAccessToken,

    // Refresh token methods
    getRefreshToken,

    // Subdomain methods
    getSubDomainInSession,
    saveSubDomainInSession,

    // Workday user methods
    getIsWorkdayUser,
    setIsWorkdayUser,
    deleteIsWorkdayUser,

    // CSRF methods
    getCsrf,
    setCsrf,

    // Zendesk methods
    getZendeskUrl,
    getZendeskExpire,
    setZendeskUrl,
    removeZendeskFlag,

    // OAuth methods
    getOAuthVars,
    getOAuthExpire,
    setOAuthVars,
    removeOAuthFlag,

    // Token timing methods
    getAccessTokenCreationTime,
    saveJwtTokenCreationTime,

    // Session validation methods
    isAegisAuthed,
    isRolodexAuthed,
    isEmbed,
    isLoggedIn,
    isAuthSessionExpired,
    isInactiveTimeoutReached,

    // Inactive timer methods
    removeInactiveTimer,
    resetInactiveTimer,

    // Other getters
    getInactiveTimeoutInSeconds,
    getCacheEncryptionKey,
  };
};

export default AuthenticationStore;
